import { BehaviorSubject, combineLatest, iif, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { BaseDomainModel } from '../models/base/base-domain-model';
import { debounceTime, distinctUntilChanged, switchMap, take, tap } from 'rxjs/operators';
import { LocationDomainModel } from './location-domain-model';
import { CompanyDomainModel } from './company-domain-model';
import { FiltersRepository } from '../repositories/filters/filters-repository';
import { FetchOptions } from '../repositories/base/fetch-strategy';
import { ToastService } from '../modules/toasts/service/toast.service';
import { BsError } from '../models/shared/bs-error';
import { HighGuideFilter } from '../models/guide/high-guide-filter';

// Provided by Logged In Scope
@Injectable()
export class FiltersDomainModel extends BaseDomainModel {
  constructor(
    protected filtersRepository: FiltersRepository,
    protected companyDomainModel: CompanyDomainModel,
    protected locationDomainModel: LocationDomainModel,
    protected toastService: ToastService
  ) {
    super();
    this.fetchFiltersForLocation();
  }

  protected readonly _filters = new BehaviorSubject<HighGuideFilter[] | null>(null);
  public readonly filters$ = this._filters as Observable<HighGuideFilter[] | null>;

  public getFilters$(ids?: string[], opts?: FetchOptions): Observable<HighGuideFilter[] | null> {
    return combineLatest([this.companyDomainModel.companyId$, this.locationDomainModel.locationId$]).pipe(
      take(1),
      switchMap(([companyId, locationId]) => {
        return ids?.length > 0
          ? this.filtersRepository.getFiltersByIds$(ids)
          : this.filtersRepository.getFilters$(companyId, locationId, opts);
      }),
      tap(filters => this._filters.next(filters))
    );
  }

  fetchFiltersForLocation(): void {
    this.locationDomainModel.locationId$
      .pipe(
        tap(_ => this._filters.next(null)),
        distinctUntilChanged(),
        debounceTime(100),
        switchMap(locationId => {
          // cache-then-fresh: a location switch is a complete reload of this scope, so a warm start is worth the
          // extra emission. `_filters` is reset to null above, so the cached emission fills a pipeline that is
          // already in its loading state.
          return iif(() => !locationId, of(null), this.getFilters$(null, { strategy: 'cache-then-fresh' }));
        })
      )
      .subscribeWhileAlive({
        owner: this,
        error: (err: BsError) => this.toastService.publishError(err)
      });
  }
}
